import React, { useEffect, useState } from "react";
import "../Stock/Stock.css";
import { ToastContainer, toast } from "react-toastify";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { UilCheck } from "@iconscout/react-unicons";
import { userRequest } from "../../RequestMethods";
import { markAlertsSeen } from "../../utils/lowStockAlertsSeen";

const STATUS_OPTIONS = ["", "open", "acknowledged", "resolved"];

const StockAlerts = () => {
  const [products, setProducts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [alertsError, setAlertsError] = useState(null);
  const [filters, setFilters] = useState({ productId: "", status: "" });

  const fetchProducts = async () => {
    try {
      const res = await userRequest.get("products/dropdown", { params: { _: Date.now() } });
      setProducts(res.data.products || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors du chargement des produits.");
    }
  };

  const fetchAlerts = async () => {
    try {
      setAlertsLoading(true);
      const params = {};
      if (filters.productId) params.productId = filters.productId;
      if (filters.status) params.status = filters.status;

      const res = await userRequest.get("stock/alerts", { params });
      const fetchedAlerts = res.data.alerts || [];
      setAlerts(fetchedAlerts);
      setAlertsError(null);
      markAlertsSeen(fetchedAlerts.map((alert) => alert._id));
    } catch (err) {
      setAlertsError(err?.response?.data?.message || "Erreur lors du chargement des alertes de stock.");
    } finally {
      setAlertsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    fetchAlerts();
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await userRequest.post(`stock/alerts/${alertId}/acknowledge`);
      toast.success("Alerte prise en compte.");
      fetchAlerts();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Impossible de prendre en compte l'alerte.");
    }
  };

  return (
    <div className="Stock">
      <h1>Alertes de stock bas</h1>

      <section className="stock-section">
        <form className="stock-filters" onSubmit={handleApplyFilters}>
          <select name="productId" value={filters.productId} onChange={handleFilterChange}>
            <option value="">Tous les produits</option>
            {products.map((product) => (
              <option key={product._id} value={product._id}>{product.title || product._id}</option>
            ))}
          </select>
          <select name="status" value={filters.status} onChange={handleFilterChange}>
            <option value="">Tous les statuts</option>
            {STATUS_OPTIONS.filter(Boolean).map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <button type="submit" className="action-icon-btn btn-primary">Filtrer</button>
        </form>

        {alertsLoading ? (
          <div className="loading-container">
            <div className="loading-spinner-custom"></div>
          </div>
        ) : alertsError ? (
          <div className="error-message-custom">
            <span className="error-icon">⚠️</span>
            <p>{alertsError}</p>
            <button className="retry-btn" onClick={fetchAlerts}>Réessayer</button>
          </div>
        ) : (
          <TableContainer component={Paper} className="stock-table-container">
            <Table>
              <TableHead>
                <TableRow className="table-header">
                  <TableCell sx={{ fontWeight: "bold" }}>Produit</TableCell>
                  <TableCell>Couleur / Taille</TableCell>
                  <TableCell>Quantité au déclenchement</TableCell>
                  <TableCell>Disponible actuel</TableCell>
                  <TableCell>Seuil actuel</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Déclenchée le</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alerts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">Aucune alerte trouvée.</TableCell>
                  </TableRow>
                )}
                {alerts.map((alert) => (
                  <TableRow key={alert._id}>
                    <TableCell>{alert.productTitle}</TableCell>
                    <TableCell>
                      {alert.inventoryItemId?.color || "—"} / {alert.inventoryItemId?.size}
                    </TableCell>
                    <TableCell>{alert.currentQty}</TableCell>
                    <TableCell>{alert.inventoryItemId?.availableQty ?? "—"}</TableCell>
                    <TableCell>{alert.inventoryItemId?.minThreshold ?? "—"}</TableCell>
                    <TableCell>
                      <span className={`badge alert-${alert.status}`}>{alert.status}</span>
                    </TableCell>
                    <TableCell>{new Date(alert.triggeredAt).toLocaleString("fr-FR")}</TableCell>
                    <TableCell>
                      {alert.status === "open" && (
                        <button className="action-icon-btn btn-primary" onClick={() => handleAcknowledge(alert._id)}>
                          <UilCheck /> Prendre en compte
                        </button>
                      )}
                      {alert.status === "acknowledged" && (
                        <span className="ack-note">
                          Pris en compte par {alert.acknowledgedBy?.name || "—"}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </section>

      <ToastContainer position="top-right" />
    </div>
  );
};

export default StockAlerts;
